import { Operations } from "../material-ui/NavigatorData";
import { CommonConfig, ProgressListenerFunction, RequestStatus } from "./commonConfig";

export interface FileResource {
  type: string,
  uri: string,
  size: number
}

export interface ErrorInfo {
  errorId: string,
  errorCode: number,
  errorMessage: string,
}

export interface JobInputs {
  a?: FileResource,
  b?: FileResource,
  ancestor?: FileResource,
  edit1?: FileResource,
  edit2?: FileResource,
  changeset?: FileResource,
  target?: FileResource
}

export interface ServerJobStatus {
  status: RequestStatus,
  lastStageRun: string,
  error: string
}

export interface RawJob {
  timeStart: string
  timeCreate: string
  timeComplete: string
  comparisonTime: number,
  jobId: string
  numberOfStages: number,
  progressInPercentage: number,
  pipelineStage: string,
  jobStatus: string
  output: FileResource
  phaseDescription: string,
  error?: ErrorInfo
}

export interface RawJobConfiguration {
  wordByWord: string | undefined,
  threeWayMergeMode: string | undefined,
  mergePriority: string | undefined,
  graftResolutionMode: string | undefined,
  arrayAlignment: string | undefined,
  patchDirection: string | undefined
}

export interface RawJobHistory extends RawJob {
  jobDescriptor: string,
  operationType: Operations,
  parameterSettings: {
    inputs: JobInputs,
    configuration: RawJobConfiguration
  },
  serverJobStatus: ServerJobStatus
  resultFilename: string
}

export abstract class CommonAction {

  public static readonly jobProgressProperties = [
    'progressInPercentage',
    'pipelineStage',
    'jobStatus']

  public static convertToBlobIfString(aFile: any) {
    if (typeof aFile === 'string') {
      const aString = aFile as string;
      const blob = new Blob([aString], { type: 'application/json' });
      aFile = blob;
    }
    return aFile;
  }  
  static readonly fileFieldA = "ajson";
  static readonly fileFieldB = "bjson";
  static readonly fileFieldChangeSet = "changeset";
  static readonly fileFieldTarget = "target";

  baseURL: string;
  commonURL: string;
  internalState = "READY";
  intervalId: any = null;
  progressListener!: ProgressListenerFunction;
  pollingIntervalDefault = 500;
  pollingInterval = this.pollingIntervalDefault;
  //maxPollNumber = 1000;
  //pollCounter = 0;
  rawJobData: RawJob|undefined;
  operation: Operations;


  jobUrl!: string;
  downloadUrl!: string;

  constructor(baseURL: string, urlPart: string, operation: Operations) {
    let interimUrl = baseURL.endsWith('/') ? baseURL : baseURL + '/';
    this.baseURL = interimUrl;
    this.commonURL = this.baseURL + urlPart;
    this.pollingInterval = this.pollingIntervalDefault;
    this.operation = operation;
  }

  downloadRequest = (resolve: Function, reject: Function, idToken: string | null) => {
    // download request function
    fetch(this.downloadUrl, {
      method: 'GET',
      headers: { 
        "Authorization": "Bearer " + idToken,
        'Accept': 'application/json',
        'DX-User-Agent': 'DeltaJsonWebApp/1.0.0'
       }
    })
      .then(res => res.json())
      .then((data) => {
        resolve([data, this.rawJobData]);
        this.setComparisonStatus('COMPLETED', null);
      })
      .catch((error) => { reject(error) })
  }

  jobRequest = (resolve: Function, reject: Function, idToken: string | null) => {
    // job request function
    // this.pollCounter++;
    // if (this.pollCounter > this.maxPollNumber) {
    //   clearInterval(this.intervalId);
    //   reject({ errorStatus: 'Max poll number exceeded: ' + this.pollCounter });
    // }
    fetch(this.jobUrl, {
      method: 'GET',
      headers: {
        "Authorization": "Bearer " + idToken,
        'Accept': 'application/json',
        'DX-User-Agent': 'DeltaJsonWebApp/1.0.0'
      }
    })
      .then(res => res.json())
      .then((data) => {
        this.rawJobData = data;
        this.setComparisonStatus(data.jobStatus, data);
        if (data.jobStatus === RequestStatus.finished) {
          this.downloadUrl = this.baseURL + "downloads/" + data.jobId;
          clearInterval(this.intervalId);
          this.downloadRequest(resolve, reject, idToken);
        } else if (data.jobStatus === RequestStatus.failed) {
          clearInterval(this.intervalId);
          reject(data);
        }
      })
      .catch((error) => {
        clearInterval(this.intervalId);
        reject(error);
      })
  }

  handleResponseObject = (data: any, resolve: Function, reject: Function, idToken: string | null) => {
    //this.pollCounter = 0;
    if (data.errorCode || data.jobStatus === RequestStatus.failed) {
      throw data;
    } else if (data.jobStatus) {
      this.setComparisonStatus(data.jobStatus, data);
      this.jobUrl = this.baseURL + "jobs/" + data.jobId;
      this.rawJobData = data;
      this.intervalId = setInterval(() => { this.jobRequest(resolve, reject, idToken) }, this.pollingInterval); // end setInterval
    } else {
      throw { errorMessage: "Missing jobStatus property in response"}
    }
  }

  public addProgressListener(progressListener: ProgressListenerFunction) {
    this.progressListener = progressListener;
    return this;
  }   

  // valid values: 0.1 seconds to 10 seconds
  public setPollingInterval(interval: number) {
    if (interval > 99 && interval < 10001) {
      this.pollingInterval = interval;
    }
    return this;
  }

  // public setMaxPollNumber(pollNumber: number) {
  //   if (pollNumber > 0) {
  //     this.maxPollNumber = pollNumber;
  //   }
  //   return this;
  // }

  setComparisonStatus(statusMessage: string, statusObject: any) {
    // show status
    if (this.progressListener) {
      this.progressListener(statusMessage, statusObject, this.operation);
    }
  }

  public action = (commonData: CommonConfig, idToken: string | null): Promise<any> => {

    return new Promise((resolve, reject) => {
      this.setComparisonStatus("REQUESTING...", null);
      let formData = new FormData();
      commonData.populateFormData(formData);
      formData.append('async', 'true');

      fetch(this.commonURL, {
        method: "POST",
        headers: {
          "Authorization": "Bearer " + idToken,
          "Accept": "application/json",
          'DX-User-Agent': 'DeltaJsonWebApp/1.0.0'
        },
        body: formData
      }).then(res => res.json())
        .then((data) => { this.handleResponseObject(data, resolve, reject, idToken) })
        .catch((error) => {
          if (error.errorCode) {
            reject(error);
          } else {
            reject({
              errorMessage: error.message,
              URL: this.commonURL
            })
          }
        })
    });
  }

  public getJobsAction = (idToken: string | null): Promise<any> => {

    return new Promise((resolve, reject) => {

      fetch(this.commonURL, {
        method: "GET",
        headers: {
          "Authorization": "Bearer " + idToken,
          "Accept": "application/json"
        }
      }).then(res => res.json())
        .then((data) => { this.handleResponseObject(data, resolve, reject, idToken) })
        .catch((error) => {
          if (error.errorCode) {
            reject(error);
          } else {
            reject({
              errorMessage: error.message,
              URL: this.commonURL
            })
          }
        })
    });
  }
}